import { useEffect, useState } from "react";
import { api, fmtMoney, fmtNum, relTime } from "@/lib/api";
import { Link } from "react-router-dom";
import MyTeamWidget from "@/components/MyTeamWidget";
import {
  TrendUp, FolderOpen, Warning, CurrencyInr, Eye, MapTrifold, ShieldCheck, CircleNotch
} from "@phosphor-icons/react";

const OSINT_MAP = "https://static.prod-images.emergentagent.com/jobs/4545735e-77e3-490b-85fb-3bc856dbec8e/images/2363b00f350c042171179770774587cb8ad0f3e73528cbf014a1bed124e9cfe2.png";

const Kpi = ({ label, value, sub, Icon, tone = "default" }) => (
  <div className="card-flat p-5">
    <div className="flex items-start justify-between">
      <div>
        <div className="label-eyebrow">{label}</div>
        <div className={`font-display font-bold mt-1 tnum text-[28px] leading-none ${tone === "danger" ? "text-[var(--span-danger)]" : ""}`}>
          {value}
        </div>
        {sub && <div className="text-[11px] text-[var(--span-text-2)] mt-2 font-mono">{sub}</div>}
      </div>
      {Icon && <Icon size={18} className="text-[var(--span-text-2)]" weight="regular" />}
    </div>
  </div>
);

export default function Dashboard() {
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/metrics");
        setM(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="font-mono text-xs text-[var(--span-text-2)]" data-testid="dashboard-loading">LOADING METRICS…</div>;
  if (!m) return null;

  const t = m.totals;
  const codeEntries = Object.entries(m.by_code).sort((a, b) => b[1] - a[1]);
  const countryEntries = Object.entries(m.by_country).sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-eyebrow">Executive Dashboard</div>
          <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Operational Command</h1>
          <div className="text-[12px] text-[var(--span-text-2)] mt-1 font-mono">
            {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/cases" className="span-btn span-btn-ghost" data-testid="open-cases-cta">
            <FolderOpen size={14} /> All cases
          </Link>
          <Link to="/escalations" className="span-btn" data-testid="open-escalations-cta">
            <Warning size={14} /> Escalation queue · {t.escalations_open}
          </Link>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="kpi-grid">
        <Kpi label="Active cases" value={fmtNum(t.cases)} sub={`${t.open} open · ${t.in_progress} in progress · ${t.closed} closed`} Icon={FolderOpen} />
        <Kpi label="CEO escalations" value={fmtNum(t.escalations_open)} sub="auto-flagged · high value / sensitive" Icon={Warning} tone={t.escalations_open > 0 ? "danger" : "default"} />
        <Kpi label="Revenue YTD" value={fmtMoney(t.revenue_inr, "INR")} sub={`${fmtMoney(t.revenue_usd, "USD")} equivalent`} Icon={TrendUp} />
        <Kpi label="Payables on hold" value={fmtNum(t.payables_hold)} sub={`${t.payables_ready} ready · ${t.payables_approved} approved`} Icon={CurrencyInr} />
      </div>

      {/* Track A § A10 — My Team widget (auto-hidden for users without direct reports) */}
      <MyTeamWidget />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* OSINT / Map */}
        <div className="card-flat lg:col-span-2 overflow-hidden" data-testid="osint-card">
          <div className="px-5 py-4 border-b border-[var(--span-border)] flex items-center justify-between">
            <div>
              <div className="label-eyebrow">Global Intelligence</div>
              <div className="font-display font-bold text-[17px]">Multi-jurisdictional Coverage</div>
            </div>
            <Link to="/intelligence" className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--span-text-2)] hover:text-black">
              View signals →
            </Link>
          </div>
          <div className="relative h-[300px] bg-black">
            <img src={OSINT_MAP} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/0 to-black/40" />
            <div className="absolute bottom-4 left-4 right-4 grid grid-cols-4 gap-px bg-white/10 text-white">
              {countryEntries.slice(0, 4).map(([k, v]) => (
                <div key={k} className="bg-black/60 backdrop-blur-sm px-3 py-2">
                  <div className="font-mono text-[9px] tracking-[0.2em] opacity-60">COUNTRY</div>
                  <div className="font-mono text-[14px] font-semibold flex items-baseline gap-2">
                    {k} <span className="text-[10px] opacity-60 tnum">{v} cases</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Escalations */}
        <div className="card-flat" data-testid="escalations-card">
          <div className="px-5 py-4 border-b border-[var(--span-border)] flex items-center justify-between">
            <div>
              <div className="label-eyebrow">CEO Queue</div>
              <div className="font-display font-bold text-[17px]">Escalations</div>
            </div>
            <span className="badge badge-danger">{t.escalations_open} OPEN</span>
          </div>
          <div className="divide-y divide-[var(--span-border)]">
            {m.recent_escalations.length === 0 && (
              <div className="px-5 py-6 text-[12px] text-[var(--span-text-2)]">No active escalations.</div>
            )}
            {m.recent_escalations.map((e) => (
              <Link to={`/cases/${e.case_id}`} key={e.escalation_id} className="block px-5 py-3 hover:bg-[var(--span-surface-2)]">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[11px] font-semibold">{e.case_code}</span>
                  <span className="text-[10px] text-[var(--span-text-2)] font-mono">{relTime(e.triggered_at)}</span>
                </div>
                <div className="text-[12px] mt-1">{e.reason}</div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Cases by code */}
        <div className="card-flat" data-testid="by-code-card">
          <div className="px-5 py-4 border-b border-[var(--span-border)]">
            <div className="label-eyebrow">Distribution</div>
            <div className="font-display font-bold text-[17px]">Cases by Type</div>
          </div>
          <div className="px-5 py-4 space-y-2">
            {codeEntries.slice(0, 8).map(([k, v]) => {
              const max = codeEntries[0][1];
              const pct = (v / max) * 100;
              return (
                <div key={k} className="flex items-center gap-3">
                  <span className="badge w-12 justify-center">{k}</span>
                  <div className="flex-1 h-1.5 bg-[var(--span-surface-2)] relative">
                    <div className="absolute inset-y-0 left-0 bg-black" style={{ width: `${pct}%` }}></div>
                  </div>
                  <span className="font-mono text-[12px] w-8 text-right tnum">{v}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Live ops */}
        <div className="card-flat" data-testid="live-ops-card">
          <div className="px-5 py-4 border-b border-[var(--span-border)]">
            <div className="label-eyebrow">Live Operations</div>
            <div className="font-display font-bold text-[17px]">Active Workstreams</div>
          </div>
          <ul className="px-5 py-4 space-y-3">
            {[
              { Icon: MapTrifold, label: "Field tasks", value: t.field_open, to: "/field-ops" },
              { Icon: Eye, label: "Intel signals", value: t.intel_open, to: "/intelligence" },
              { Icon: ShieldCheck, label: "Quality reviews", value: "—", to: "/quality" },
              { Icon: CircleNotch, label: "Cases in progress", value: t.in_progress, to: "/cases" },
            ].map((r, i) => (
              <li key={i} className="flex items-center justify-between">
                <Link to={r.to} className="flex items-center gap-2 text-[13px] hover:underline">
                  <r.Icon size={14} weight="regular" /> {r.label}
                </Link>
                <span className="font-mono text-[14px] font-semibold tnum">{r.value}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* FX */}
        <div className="card-flat" data-testid="fx-card">
          <div className="px-5 py-4 border-b border-[var(--span-border)]">
            <div className="label-eyebrow">FX Exposure</div>
            <div className="font-display font-bold text-[17px]">Daily Rates · INR base</div>
          </div>
          <FxList />
        </div>
      </div>
    </div>
  );
}

function FxList() {
  const [r, setR] = useState([]);
  useEffect(() => { api.get("/fx-rates").then(({ data }) => setR(data)); }, []);
  return (
    <div className="px-5 py-4 grid grid-cols-2 gap-x-6 gap-y-2 text-[12px]">
      {r.filter(x => x.code !== "INR").map(x => (
        <div key={x.code} className="flex items-center justify-between">
          <span className="font-mono">{x.code}</span>
          <span className="font-mono tnum">{Number(x.base_rate_to_inr ?? 0).toFixed(2)}</span>
        </div>
      ))}
    </div>
  );
}
